import { useEffect, useRef, useState } from 'react';
import type { Contract } from '../../../../types';
import { addContractAttachment } from '../../../../store';
import { uploadFile, uploadFailureMessage, getFileDownloadUrl } from '../../../../api/file';
import { showToast } from '../../../../components/base/Toast';

/** One stored document: an image shows as a thumbnail, anything else (or an
 * image that fails to load) as a file row. Both open full-size in a new tab. */
function AttachmentTile({ fileId, type, caption }: { fileId: string; type: 'image' | 'doc'; caption?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getFileDownloadUrl(fileId)
      .then((u) => { if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [fileId]);

  if (failed) {
    return (
      <div className="flex h-20 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 px-2 text-center text-[11px] text-slate-400">
        Không mở được tệp
      </div>
    );
  }
  if (!url) return <div className="h-20 animate-pulse rounded-lg bg-slate-100" />;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={caption || 'Xem tài liệu'}
      className="block overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
    >
      {type === 'image' && !broken ? (
        <img src={url} alt={caption || 'Tài liệu hợp đồng'} onError={() => setBroken(true)} className="h-20 w-full object-cover" />
      ) : (
        <div className="flex h-20 flex-col items-center justify-center gap-1 px-2">
          <i className="ri-file-text-line text-2xl text-slate-500" />
          <span className="w-full truncate text-center text-[10px] text-slate-600">{caption || 'Tài liệu'}</span>
        </div>
      )}
    </a>
  );
}

/**
 * Documents on a contract card: what is stored is always visible and opens in
 * a new tab; the HTX can add more at any point in the contract's life (terms
 * are immutable after DRAFT, but a scan of the signed copy or an annex often
 * arrives later). A picked file shows straight away while it uploads.
 */
export function ContractAttachments({ contract, farmerId, htxId, canEdit }: { contract: Contract; farmerId: string; htxId?: string; canEdit: boolean }) {
  const attachments = contract.attachments ?? [];
  const inputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<{ url: string; isImage: boolean; name: string } | null>(null);

  useEffect(() => {
    if (!pending) return;
    return () => URL.revokeObjectURL(pending.url);
  }, [pending]);

  async function handleFile(file: File) {
    const isImage = file.type.startsWith('image/');
    setPending({ url: URL.createObjectURL(file), isImage, name: file.name });
    try {
      const fileId = await uploadFile(file, 'CONTRACT_DOCUMENT', file.type || 'application/octet-stream');
      await addContractAttachment(contract.id, { fileId, type: isImage ? 'image' : 'doc', caption: file.name }, farmerId, htxId);
      showToast('Đã đính kèm tài liệu vào hợp đồng', 'success');
    } catch (err) {
      showToast(uploadFailureMessage(err, 'Tài liệu hợp đồng'), 'error');
    } finally {
      setPending(null);
    }
  }

  if (attachments.length === 0 && !canEdit) return null;

  return (
    <div className="mt-2.5 border-t border-slate-100 pt-2.5">
      <p className="mb-1.5 text-[11px] font-semibold text-slate-600">
        Tài liệu đính kèm ({attachments.length})
      </p>
      {attachments.length === 0 && !pending && <p className="mb-1.5 text-[11px] text-slate-400">Chưa có tài liệu nào.</p>}
      {(attachments.length > 0 || pending) && (
        <div className="mb-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {attachments.map((a) => (
            <AttachmentTile key={a.fileId} fileId={a.fileId} type={a.type} caption={a.caption} />
          ))}
          {pending && (
            <div className="relative h-20 overflow-hidden rounded-lg border border-sky-200 bg-slate-50">
              {pending.isImage ? (
                <img src={pending.url} alt={pending.name} className="h-full w-full object-cover opacity-60" />
              ) : (
                <div className="flex h-full items-center justify-center px-2 text-center text-[10px] text-slate-600">{pending.name}</div>
              )}
              <span className="absolute inset-x-0 bottom-0 bg-white/85 py-0.5 text-center text-[10px] text-sky-700">Đang tải lên...</span>
            </div>
          )}
        </div>
      )}
      {canEdit && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,application/pdf,.doc,.docx"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void handleFile(file);
            }}
          />
          <button
            type="button"
            disabled={Boolean(pending)}
            onClick={() => inputRef.current?.click()}
            className="h-8 w-full rounded-lg border border-dashed border-agrida-300 bg-agrida-50 text-xs font-semibold text-agrida-700 disabled:opacity-60"
          >
            <i className="ri-attachment-2 mr-1" />
            Đính kèm tài liệu
          </button>
        </>
      )}
    </div>
  );
}
